import { viajaAhorrando as viajaAhorrandoApi } from 'js-api-client';
import * as types from 'constants/ActionTypes';
import { setError } from '@/actions';
import errorMonitoring from 'errorMonitoring';
import {
  extractErrorData,
  getViajaAhorrandoErrorKey,
  shouldRemoveVivaPnrFromUrl,
  removeVivaPnrFromUrl,
} from 'utils/viajaAhorrando/errorHandling';
import { setDiscountedTrips } from './search';

/**
 * Set discounts loading state.
 * @param {boolean} isLoading - Loading state
 * @returns {Object} Action object
 */
export const setDiscountsLoading = (isLoading) => ({
  type: types.SET_VIAJA_AHORRANDO_DISCOUNTS_LOADING,
  isLoading,
});

/**
 * Set discounts fetched state with PNR.
 * @param {string} pnr - PNR code
 * @returns {Object} Action object
 */
export const setDiscountsFetched = (pnr) => ({
  type: types.SET_VIAJA_AHORRANDO_DISCOUNTS_FETCHED,
  pnr,
});

/**
 * Reset Viaja Ahorrando state.
 * @returns {Object} Action object
 */
export const resetViajaAhorrando = () => ({
  type: types.RESET_VIAJA_AHORRANDO,
});

/**
 * Get discounts for Viaja Ahorrando.
 *
 * This action does two things:
 * 1. Saves request state in search.viajaAhorrando reducer
 * 2. Saves discounted trips in search reducer (reusing coupon pattern)
 *
 * Gets search IDs directly from Redux state (similar to walletSearchDiscount pattern).
 *
 * @param {Object} params - Discount params
 * @param {string} params.pnr - PNR code
 * @returns {Function} Thunk function that dispatches actions
 */
export const getDiscounts = ({ pnr }) => {
  return async (dispatch, getState) => {
    dispatch(setDiscountsLoading(true));
    try {
      // Get search IDs directly from Redux state (same pattern as walletSearchDiscount)
      const { search: searchState } = getState();
      const departSearchId = searchState.getIn(['departure', 'id']);
      const returnSearchId = searchState.getIn(['return', 'id']);
      const searchIds = [departSearchId, returnSearchId].filter(Boolean);
      // eslint-disable-next-line camelcase
      const searchIdsString = searchIds.join(',');

      // Call real API
      // eslint-disable-next-line camelcase
      const response = await viajaAhorrandoApi.getDiscounts({
        // eslint-disable-next-line camelcase
        search_ids: searchIdsString,
        pnr,
      });

      // 1. Save state in search.viajaAhorrando reducer (for banner, status, etc.)
      dispatch(setDiscountsFetched(pnr));

      // 2. Save discounted trips in search reducer (reusing coupon pattern)
      // Uses fixed key 'viajaAhorrando' in discountedTripsByCoupon
      dispatch(setDiscountedTrips('viajaAhorrando', response));

      return response;
    } catch (error) {
      const errorData = extractErrorData(error);
      const errorCode = errorData.code;

      // Reset loading state to allow retry attempts
      dispatch(setDiscountsLoading(false));

      // Remove viva_pnr from URL for NotFound and Expired errors
      if (shouldRemoveVivaPnrFromUrl(errorCode)) {
        removeVivaPnrFromUrl();
      }

      // Get translation key based on error code
      const errorKey = getViajaAhorrandoErrorKey(errorCode);

      // Show error alert to user
      dispatch(setError(errorData.status || 500, errorKey, 'warning', false));

      errorMonitoring.notify({ error, info: 'Error fetching Viaja Ahorrando discounts' });
    }
  };
};
